import { createRootRoute, Outlet, redirect } from '@tanstack/react-router';

import { TelemetryErrorBoundary } from '../components/TelemetryErrorBoundary';
import { TelemetryProvider } from '../lib/telemetry-react';

/**
 * Root route — single layout shell for the whole app.
 *
 * `beforeLoad` is the canonical TanStack-Router auth gate (see WW-SEC-302,
 * WW-SEC-400). The data layer is correct (every protected /api/* route 401s
 * an anonymous request and 403s an under-privileged one) but without a
 * client-side gate the IA leaks: anonymous users hitting `/dashboard` get
 * the AppShell rendered with empty placeholders, and an under-privileged
 * signed-in user hitting `/host/...` or `/admin/...` gets the entire shell
 * + permanent "Loading…" tiles. We close that here.
 *
 * Wave 1 dual-role: `users.role` is no longer used for gating. The /me
 * endpoint returns a `capabilities` triple {isArtist, isHost, isAdmin}
 * derived from artist_profile / venue_account presence + users.isAdmin.
 * Path rules:
 *   • Protected paths require any authenticated user.
 *   • `/host/*`, `/events/manage`, `/events/new` require `isHost`.
 *   • `/admin/*` requires `isAdmin`.
 *
 * The auth context (`useAuth`) still carries the in-render copy of /me;
 * `beforeLoad` does its own fetch because the context isn't yet on the
 * router context object. Both call /api/auth/me so the worker is single
 * source of truth.
 */
export const Route = createRootRoute({
  beforeLoad: async ({ location }) => {
    // FR-081: the router matches routes case-insensitively (caseSensitive
    // defaults false), so /Dashboard or /HOST/venues resolve to the real
    // components — but the prefix checks below compare raw pathname, so a
    // case-varied URL used to skip the guard entirely. Normalise once here.
    // (The sign-in redirect still uses original-case location.href below.)
    const pathname = location.pathname.toLowerCase();

    if (!isProtectedPath(pathname)) return;

    const me = await fetchMe();

    if (!me || !me.user) {
      // Preserve the destination (path + search + hash) so we can land them
      // back here after sign-in. Encoding via `redirect` query param.
      throw redirect({
        to: '/sign-in',
        search: { redirect: location.href },
      });
    }

    const caps = me.capabilities ?? { isArtist: false, isHost: false, isAdmin: false };

    if (isAdminPath(pathname) && !caps.isAdmin) {
      throw redirect({ to: '/dashboard' });
    }
    if (isHostPath(pathname) && !caps.isHost) {
      throw redirect({ to: '/dashboard' });
    }
    if (isArtistPath(pathname) && !caps.isArtist) {
      throw redirect({ to: '/dashboard' });
    }
  },
  component: RootShell,
});

function RootShell() {
  return (
    <TelemetryProvider>
      <TelemetryErrorBoundary>
        <Outlet />
      </TelemetryErrorBoundary>
    </TelemetryProvider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Path classification
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Authenticated routes — anything that renders a signed-in user's data or
 * the AppShell with personalised content.
 *
 * Listed as exact match or prefix (`/foo` matches `/foo` and `/foo/...`).
 * Order doesn't matter; we accept any match.
 */
const PROTECTED_PREFIXES: readonly string[] = [
  '/dashboard',
  '/inbox',
  '/exchanges',
  '/profile',
  '/settings',
  '/notifications',
  '/billing',
  '/verification',
  '/host',
  '/admin',
  '/disputes',
  '/request',
  '/onboarding',
  // Wave B return-hooks
  '/tours',
  // FR-080: authenticated surfaces that render AppShell unconditionally and
  // were missing from the guard, so an anonymous visitor got the app chrome
  // (misleading placeholder content) instead of a sign-in redirect.
  // /artist-billing is NOT covered by /billing (matchesPrefix needs an exact or
  // '/billing/'-prefixed path).
  '/availability',
  '/artist-billing',
  '/certificates',
  // FR-139 removed '/sign-out' — the page handles a missing session on its own,
  // and gating it created a sign-in-then-instantly-signed-out redirect trap.
];

const ADMIN_PREFIXES: readonly string[] = ['/admin'];

/**
 * Host capability paths. /host/* is the existing host shell. /events/new and
 * /events/manage are Wave 2 surfaces that the events slice will land — we
 * gate them here so the slice agent doesn't have to re-derive the rule.
 * /events/:id detail pages are public (read-only event browsing) and stay
 * out of this list.
 *
 * Wave B: /host/availability (open-weeks editor) inherits via the /host
 * prefix — no extra entry needed.
 */
// FR-138: /events/new and /events/manage never existed as routes — the real
// host event pages live under /host (/host/events/new, /host/events/$id/manage)
// and are already covered by the /host prefix. Dropped the dead entries.
const HOST_EXACT_AND_PREFIXES: readonly string[] = ['/host'];

/**
 * Artist capability paths. Wave B return-hooks: tour-route planning is
 * artist-only. The /tours prefix covers the index, new-route form, and
 * detail pages.
 */
// FR-080: /availability + /artist-billing are artist-only surfaces, so a
// signed-in host hitting them is redirected rather than landing on artist UI.
const ARTIST_PREFIXES: readonly string[] = ['/tours', '/availability', '/artist-billing'];

function matchesPrefix(pathname: string, prefixes: readonly string[]): boolean {
  for (const prefix of prefixes) {
    if (pathname === prefix || pathname.startsWith(`${prefix}/`)) return true;
  }
  return false;
}

function isProtectedPath(pathname: string): boolean {
  return matchesPrefix(pathname, PROTECTED_PREFIXES) || matchesPrefix(pathname, HOST_EXACT_AND_PREFIXES);
}

function isAdminPath(pathname: string): boolean {
  return matchesPrefix(pathname, ADMIN_PREFIXES);
}

function isHostPath(pathname: string): boolean {
  return matchesPrefix(pathname, HOST_EXACT_AND_PREFIXES);
}

function isArtistPath(pathname: string): boolean {
  return matchesPrefix(pathname, ARTIST_PREFIXES);
}

// ─────────────────────────────────────────────────────────────────────────────
// /me fetch
// ─────────────────────────────────────────────────────────────────────────────

type MeUser = {
  id: string;
  email: string;
  role: 'artist' | 'host' | 'admin';
  displayName: string;
  status: 'pending' | 'active' | 'suspended' | 'deleted';
};

type MeCapabilities = {
  isArtist: boolean;
  isHost: boolean;
  isAdmin: boolean;
};

type MeResponse = {
  user: MeUser | null;
  capabilities: MeCapabilities | null;
  activeHat: 'artist' | 'host' | 'admin' | null;
};

/**
 * Hit /api/auth/me with the session cookie. Returns the response body on 2xx,
 * `null` on any non-2xx (anonymous, suspended-redirect, or transient blip —
 * we fail closed and treat all of them as "no session").
 */
async function fetchMe(): Promise<MeResponse | null> {
  try {
    const res = await fetch('/api/auth/me', {
      method: 'GET',
      credentials: 'include',
    });
    if (!res.ok) return null;
    const body = (await res.json()) as MeResponse;
    return body;
  } catch {
    return null;
  }
}
